// @flow
import React, { type AbstractComponent } from 'react'
import { styled } from 'styled-components'
import type { Image, Video, Audio } from '@mission.io/mission-toolkit/actions'
import { ExtenderFrameStyle } from './animations'
import { ContentExpander } from './MediaEventInput'
import { ControllableVideo } from '../../MediaPopup'

export type ExtenderMedia = Image | Video | Audio
type ExtenderFrameProps = {
	style?: {},
	media: ExtenderMedia,
	isExiting: boolean,
	controlModal?: { isOpen: boolean, setIsOpen: Function },
}

/**
 * An extended portion of the frame that can be used to display additional information.
 */
export const ExtenderFrame: AbstractComponent<
	ExtenderFrameProps,
	?HTMLDivElement
> = React.forwardRef(function _ExtenderFrame(
	{ style, media, isExiting, controlModal }: ExtenderFrameProps,
	ref
): React$Node {
	const positioningRef = React.useRef()
	const [isLoadFinished, setIsLoadFinished] = React.useState(false)

	return (
		<div className="relative grow">
			<ExtenderFrameStyle
				ref={ref}
				style={style}
				$exit={isExiting}
				className="absolute z-0 top-1/2 -translate-y-1/2 w-full ">
				<div className="flex items-center">
					{isLoadFinished && <DecalExtenderFrame contentRef={positioningRef} />}
					<div
						// The left margin moves it over so that the media inside does not get cut off as much. This is not a perfect solution, as the image still gets cuts off differently at different screen sizes.
						className="relative bg-[rgba(0,255,255)]/50 border-[3px] border-game-accent-aqua ml-[1px] rounded-r-[1rem] p-3 pl-0"
						ref={positioningRef}>
						<ExtendedContent
							media={media}
							onLoadFinished={() => {
								setIsLoadFinished(true)
							}}
							controlModal={controlModal}
						/>
					</div>
				</div>
			</ExtenderFrameStyle>
		</div>
	)
})

/**
 * The content for a smaller frame frame that will roll out from the side of the literacy event frame. For most text literacy events, this will be an image.
 * @param {Image|Video|Audio} props.media - The media to display in the extended content
 * @param {{isOpen: boolean, setIsOpen: Function}} props.controlModal - A function to control the expanded modal for a media item
 * @param {Function} props.onLoadFinished - A function to call when the media has finished loading
 * @returns {React$Node}
 */
function ExtendedContent({
	media,
	controlModal,
	onLoadFinished,
}: {
	media: ExtenderMedia,
	controlModal?: { isOpen: boolean, setIsOpen: Function },
	onLoadFinished: () => void,
}): React$Node {
	const [loaded, _setLoaded] = React.useState(false)
	const setLoaded = (value: boolean) => {
		_setLoaded(value)
		if (onLoadFinished) onLoadFinished()
	}
	const [subscribingVideoRefs, setSubscribingVideoRefs] = React.useState<
		Array<{
			current: HTMLVideoElement | void,
		}>
	>([])

	const subscribe = videoRef => {
		setSubscribingVideoRefs(prev => [...prev, videoRef])
	}

	const mediaJsx = (inModal: boolean) =>
		media.type === 'IMAGE' ? (
			<img
				alt="Event Alert"
				className="overflow-hidden border-4 border-game-primary-green rounded-xl max-h-[80vh] max-w-[80vw]"
				src={media.url}
				onLoad={() => setLoaded(true)}
			/>
		) : media.type === 'VIDEO' ? (
			<div
				className={
					'max-h-full max-w-full min-h-36 aspect-video bg-black rounded-r-xl overflow-hidden'
				}>
				<ControllableVideo
					src={media.url}
					onLoad={() => setLoaded(true)}
					autoPlay={inModal}
					subscribe={subscribe}
				/>
			</div>
		) : (
			<audio
				onLoadedData={() => setLoaded(true)}
				className="pl-1 min-w-36 max-w-full"
				controls>
				<source src={media.url} />
			</audio>
		)
	// The max media height allowed differs between the controller and student view
	return (
		<>
			<div
				className={
					'[&>img]:max-h-[calc(35*var(--ph)-48px)] [&>img]:rounded-r-xl [&>img]:border-l-0 relative rounded-r-xl '
				}>
				{!loaded && (
					<div className="absolute top-0 h-full w-full">
						<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
							Loading...
						</div>
					</div>
				)}
				{mediaJsx(false)}
			</div>
			{media.type !== 'AUDIO' && loaded && (
				<ContentExpander
					modalStyles={
						media.type === 'VIDEO'
							? {
									content: {
										top: '50%',
										left: '50%',
										transform: 'translate(-50%, -50%)',
										width: '90%',
										height: '90%',
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
									},
							  }
							: {}
					}
					onExpand={
						// This is a function that will pause the video that is currently playing
						() => {
							subscribingVideoRefs[0]?.current?.pause()
						}
					}
					className="bg-[#020223] bottom-8 right-8"
					controlModal={controlModal}>
					{mediaJsx(true)}
				</ContentExpander>
			)}
		</>
	)
}

/**
 * A decorative svg design that appears on the left side of the extender frame when in the horizontal view for a literacy event.
 * @returns {React$Node}
 */
function DecalExtenderFrame({
	contentRef,
}: {
	contentRef: { current?: HTMLDivElement | null },
}): React$Node {
	const containerWidth = contentRef.current?.clientWidth || 200
	const topLineStart = Math.ceil(containerWidth * 0.8)
	const bottomLineStart = Math.ceil(containerWidth * 0.4)

	return (
		<FillerDiv>
			<AbsoluteDiv>
				<svg viewBox="0 0 10 10">
					<path d="M0 0 L10 10 L0 10 L0 0 Z" fill="#25346C" />
					<path
						d={`M10 13 ${topLineStart} 13 L${topLineStart + 10} 25`}
						className="stroke-game-accent-aqua"
						strokeWidth="3"
						fill="transparent"
					/>
				</svg>
				<div className="grow w-[10px] bg-[#25346c] z-[1]"></div>
				<svg viewBox="0 0 10 10">
					<path d="M0 0 L10 0 L0 10 L0 0 Z" fill="#25346C" />
					<path
						d={`M10 -3 L${bottomLineStart} -3 L${bottomLineStart + 10} -15`}
						className="stroke-game-accent-aqua"
						strokeWidth="3"
						fill="transparent"
					/>
					<circle
						cx={bottomLineStart + 18}
						cy="-6"
						r="5"
						className="stroke-game-accent-aqua"
						strokeWidth="3"
						fill="transparent"
					/>
					<circle
						cx={bottomLineStart + 36}
						cy="-6"
						r="5"
						className="stroke-game-accent-aqua"
						strokeWidth="3"
						fill="transparent"
					/>
				</svg>
			</AbsoluteDiv>
		</FillerDiv>
	)
}

const FillerDiv = styled.div`
	align-self: stretch;
	position: relative;
	width: 10px;
	svg {
		width: 10px;
		overflow: visible;
	}
`

const AbsoluteDiv = styled.div`
	position: absolute;
	top: -24px;
	display: flex;
	flex-direction: column;
	height: calc(100% + 48px);
`
